import { AssociationRole, Status } from '~~/openapi-client/gen/schemas'
import { RouteNames } from '~/utils/routeNames'

/**
 * Middleware, die überprüft, ob der Benutzer alle Onboarding-Schritte abgeschlossen hat.
 */
export default defineNuxtRouteMiddleware(async (to, from) => {
	const {
		membership,
		amIOnboarded,
		amIVideoLegitimized,
		doIHaveAnyMemberships,
		isMyMembershipDataComplete,
	} = useUserMembership()

	const { isAuthenticated } = useAuth()
	const { selectedAssociation, hasMultipleAssociations } = useAssociations()
	const { existsCreatedAssociation } = useCreatorAssociations()
	const { fetchStatus, verificationStatus } = useIdnow()

	// Nur lokal und auf Dev loggen: enthält Mitgliedschaft inkl. personenbezogener Daten.
	// In Stage/Prod würde dies in DevTools-Console und Sentry-Breadcrumbs landen.
	const { isLocal, isDev } = useEnvironment()
	if (isLocal || isDev) {
		console.debug({
			to: to.path,
			from: from.path,
			isAuthenticated: isAuthenticated.value,
			selectedAssociation: selectedAssociation.value,
			existsCreatedAssociation: existsCreatedAssociation.value,
			membership: membership.value,
			amIOnboarded: amIOnboarded.value,
			amIVideoLegitimized: amIVideoLegitimized.value,
			doIHaveAnyMemberships: doIHaveAnyMemberships.value,
			isMyMembershipDataComplete: isMyMembershipDataComplete.value,
		})
	}

	if (isAuthenticated.value) {
		// Nutzer hat mehrere Vereine, aber noch keinen ausgewählt → Vereinsauswahl anzeigen
		if (hasMultipleAssociations.value && !selectedAssociation.value) {
			if (amIVideoLegitimized.value) {
				if (to.path !== RouteNames.ONBOARDING.ASSOCIATION_SELECTION) {
					console.debug('User has multiple associations but none selected → navigating to association selection...')
					return navigateTo(RouteNames.ONBOARDING.ASSOCIATION_SELECTION, { replace: true })
				}
				return
			}
		}

		if (!amIOnboarded.value) {
			console.debug('User is not onboarded yet')

			switch (membership.value?.role) {
				case AssociationRole.MANAGE_USERS: // Als Mitgliederverwalter muss nichts weiter beachtet werden
					return

				default: // Frisch registrierter Nutzer, wo noch gar keine Rolle vergeben wurde
				case AssociationRole.ADMIN: // Ich bin Vorstandsmitglied
					if (!amIVideoLegitimized.value) {
						// Aktuellen Verifikationsstatus vom Backend abrufen, um die Navigation korrekt zu steuern
						if (to.path !== RouteNames.ONBOARDING.LOGIN) {
							await fetchStatus()

							switch (verificationStatus.value) {
								case Status.REVIEW_PENDING:
									if (to.path !== RouteNames.ONBOARDING.LEGITIMATION_PENDING) {
										return navigateTo(RouteNames.ONBOARDING.LEGITIMATION_PENDING, { replace: true })
									}
									return
								case Status.ABORTED:
								case Status.OTHER:
									// /legitimation (Methodenauswahl) bleibt erreichbar, damit Nutzer nach
									// einem Abbruch zwischen Video-Ident und Face-Ident neu wählen können.
									if (
										to.path !== RouteNames.ONBOARDING.LEGITIMATION_ABORTED
										&& to.path !== RouteNames.ONBOARDING.LEGITIMATION
									) {
										return navigateTo(RouteNames.ONBOARDING.LEGITIMATION_ABORTED, { replace: true })
									}
									return
								case Status.FRAUD:
									if (to.path !== RouteNames.ONBOARDING.LEGITIMATION_FRAUD) {
										return navigateTo(RouteNames.ONBOARDING.LEGITIMATION_FRAUD, { replace: true })
									}
									return
								case Status.SUCCESS:
									if (existsCreatedAssociation.value || doIHaveAnyMemberships.value) {
										if (to.path !== RouteNames.ONBOARDING.ASSOCIATION_BOARDMEMBERSHIP.CREATE) {
											return navigateTo(RouteNames.ONBOARDING.ASSOCIATION_BOARDMEMBERSHIP.CREATE, { replace: true })
										}
									} else if (to.path !== RouteNames.ONBOARDING.ASSOCIATION_REGISTRATION) {
										return navigateTo(RouteNames.ONBOARDING.ASSOCIATION_REGISTRATION, { replace: true })
									}
									return
							}
						}

						// Ich habe noch gar keine Mitgliedschaft
						if (!membership.value && (to.path !== RouteNames.ONBOARDING.INTRODUCTION && !to.path.startsWith(RouteNames.ONBOARDING.LEGITIMATION))) {
							console.debug('User has no membership yet → navigating to introduction...')
							return navigateTo(RouteNames.ONBOARDING.INTRODUCTION, { replace: true })
						} else {
							// Ich habe eine Mitgliedschaft, aber meine Daten sind noch nicht vollständig für eine Legitimation
							if (doIHaveAnyMemberships.value && !isMyMembershipDataComplete.value && to.path !== RouteNames.ONBOARDING.ASSOCIATION_BOARDMEMBERSHIP.UPDATE) {
								console.debug('User has incomplete membership data → navigating to association board membership...')
								return navigateTo(RouteNames.ONBOARDING.ASSOCIATION_BOARDMEMBERSHIP.UPDATE, { replace: true })
							}

							if (isMyMembershipDataComplete.value && !to.path.startsWith(RouteNames.ONBOARDING.LEGITIMATION)) {
								console.debug('User is not legitimized → navigating to legitimation...')
								return navigateTo(RouteNames.ONBOARDING.LEGITIMATION, { replace: true })
							}
						}
					} else {
						// Ich habe noch keinen Verein angelegt und bin auch in keinem Mitglied
						if (!existsCreatedAssociation.value && !doIHaveAnyMemberships.value && to.path !== RouteNames.ONBOARDING.ASSOCIATION_REGISTRATION) {
							console.debug('User has no association and no memberships → navigating to association registration...')
							return navigateTo(RouteNames.ONBOARDING.ASSOCIATION_REGISTRATION, { replace: true })
						}

						// Ich habe eine Membership im ausgewählten Verein, aber meine Daten sind unvollständig → Update
						if (membership.value && !isMyMembershipDataComplete.value && to.path !== RouteNames.ONBOARDING.ASSOCIATION_BOARDMEMBERSHIP.UPDATE) {
							console.debug('User has incomplete membership data in selected association → navigating to association board membership update...')
							return navigateTo(RouteNames.ONBOARDING.ASSOCIATION_BOARDMEMBERSHIP.UPDATE, { replace: true })
						}

						// Ich habe einen Verein angelegt, aber noch keine Membership im ausgewählten Verein → Vorstand anlegen
						if ((existsCreatedAssociation.value || doIHaveAnyMemberships.value) && !membership.value && to.path !== RouteNames.ONBOARDING.ASSOCIATION_BOARDMEMBERSHIP.CREATE) {
							console.debug('User has created an association but has no board membership → navigating to association board membership...')
							return navigateTo(RouteNames.ONBOARDING.ASSOCIATION_BOARDMEMBERSHIP.CREATE, { replace: true })
						}
					}
			}
		} else {
			// Wenn der Nutzer vollständig onboarded ist, darf er die Onboarding-Seiten nicht mehr betreten
			if (to.path.startsWith('/onboarding')) {
				// Vereinsauswahl ist auch für onboardete Nutzer mit mehreren Vereinen erlaubt
				if (to.path === RouteNames.ONBOARDING.ASSOCIATION_SELECTION) {
					return
				}
				console.debug('User is already onboarded → navigating to home...')
				return navigateTo(RouteNames.INDEX, { replace: true })
			}
		}
	}
})
